import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import { useImperativeHandle, useRef, useState } from 'react';
import {
	ContactTimelineTypes,
	ContactViewModel,
	RichContentViewModel,
	TimelineEventTypes,
} from '../../../../../extViewmodels';
import { AllRichContentSupportedTypes, RichContentSupportedTypes } from '../../../../../models';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getContactFullName } from '../../../../../models/utils';
import { isFilesFeatureEnabled } from '../../../../../models/utils/accountUtils';
import { invalidateContactTimeline } from '../../../../../queries';
import {
	ContactAutomationsProgressBanner,
	IContactAutomationsProgressBannerComponent,
} from '../../../../components/contacts/ContactAutomationsProgressBanner';
import { ContactConnections } from '../../../../components/contacts/ContactConnections';
import { CheckboxFilterDropdown, ICheckboxFilterOption } from '../../../../components/CheckboxFilterDropdown';
import { EntityDataBoards } from '../../../../components/dataBoards/EntityDataBoards';
import { EntityAnalysis } from '../../../../components/entities/EntityAnalysis';
import { EntityRichContent } from '../../../../components/entities/EntityRichContent';
import { ITimelineFilterOption, TimelineFilter } from '../../../../components/entities/EntityTimeline/TimelineFilter';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Label } from '../../../../components-v2/Label';
import { AnalysisIcon } from '../../../../components/svgs/icons/AnalysisIcon';
import { ConnectionsIcon } from '../../../../components/svgs/icons/ConnectionsIcon';
import { DataboardsIcon } from '../../../../components/svgs/icons/DataboardsIcon';
import { FileTextIcon } from '../../../../components/svgs/icons/FileTextIcon';
import { NotesIcon } from '../../../../components/svgs/icons/NotesIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../../components/Tabs';
import { ContactFiles } from './Files';
import { QuickActions } from './QuickActions';
import { styleSheet } from './styles';
import { ContactTimelineV2 } from './TimeLine';

const TIMELINE_FILTER_OPTIONS: ITimelineFilterOption[] = [
	{ id: 'notes', name: 'notes', label: 'Show notes', types: ['NoteEvent'] },
	{ id: 'action-items', name: 'action items', label: 'Show action items', types: ['ActionItemEvent'] },
	{ id: 'emails', name: 'emails', label: 'Show emails', types: ['SentEmailEvent', 'HtmlNewsletterEvent'] },
	{ id: 'texts', name: 'conversations', label: 'Show texts', types: ['ConversationThreadEvent'] },
	{
		id: 'outside-levitate',
		name: 'activities outside Levitate',
		label: 'Show activities outside Levitate',
		types: ['MeetingEvent', 'ExternalSentEmailEvent'],
	},
	{ id: 'phone-calls', name: 'phone calls', label: 'Show phone calls', types: ['PhoneCallEvent'] },
	{
		id: 'surveys',
		name: 'surveys',
		label: 'Show surveys',
		types: [
			'SatisfactionSurveyResponseEvent',
			'CustomSurveyResponseEvent',
			'DonationSurveyResponseEvent',
			'EventRegistrationSurveyResponseEvent',
		],
	},
	{
		id: 'handwritten-cards',
		name: 'handwritten cards',
		label: 'Show handwritten cards',
		types: ['HandwrittenCardOrderEvent'],
	},
	{
		id: 'file-events',
		name: 'file events',
		label: 'Show file events',
		types: ['FileDownloadEvent', 'FileDeleteEvent', 'FileSendEvent', 'FileRequestSurveyResponseEvent'],
	},
];

// Each option id is the rich content type it filters on, so the committed selection is also what gets
// persisted — keeping the stored format identical to the filter this replaced.
const RICH_CONTENT_FILTER_OPTIONS: ICheckboxFilterOption[] = [
	{ id: 'EmailNote', name: 'emails', label: 'Show emails' },
	{ id: 'Note', name: 'notes', label: 'Show notes' },
	{ id: 'ActionItem', name: 'action items', label: 'Show action items' },
];

const typesStorageKey = 'timelineFilterOptions';
const richContentStorageKey = 'richContentFilterOptions';
const expandedToggleStorageKey = 'timelineExpandedToggle';

export interface IContactActivityComponent {
	showRichContentTab(options?: { manageAutomationId?: string }): void;
}

enum ContactActivityDataContext {
	Analysis = 'Analysis',
	Connections = 'Connections',
	History = 'History',
	RichContent = 'RichContent',
	DataBoards = 'Data Boards',
	Timeline = 'Timeline',
	Files = 'Files',
}

interface Props {
	className?: StyleDeclarationValue[];
	contact?: ContactViewModel;
	innerRef?(ref?: IContactActivityComponent): void;
	setShouldStartPollingForRelationshipUpdate?(poll: boolean): void;
}

const getInitialRichContentTypes = () => {
	// `filter(Boolean)` drops the empty segment a blank stored value splits into: passing '' as a type
	// would otherwise reach the API, and an empty list makes the server return every type.
	const storedTypes = window.localStorage.getItem(richContentStorageKey)?.split('|').filter(Boolean);
	const richContentTypes =
		!storedTypes?.length || storedTypes.includes('All')
			? AllRichContentSupportedTypes
			: storedTypes.filter(option => option !== 'All');
	return richContentTypes as RichContentSupportedTypes[];
};

const KNOWN_TIMELINE_TYPES = new Set<string>(TIMELINE_FILTER_OPTIONS.flatMap(option => option.types));

// Values written by the company timeline (comma-joined type groups, same storage key) are not valid
// types here, so anything unrecognized is dropped rather than filtered on.
const readStoredTimelineTypes = () => {
	try {
		return window.localStorage
			.getItem(typesStorageKey)
			?.split('|')
			.filter(type => KNOWN_TIMELINE_TYPES.has(type));
	} catch {
		return undefined;
	}
};

const getInitialTimelineSupportedTypes = (): TimelineEventTypes[] => {
	const storedTypes = readStoredTimelineTypes();
	return storedTypes?.length ? (storedTypes as TimelineEventTypes[]) : ContactTimelineTypes;
};

const getSelectedTimelineFilterIds = (): string[] => {
	const storedTypes = readStoredTimelineTypes();
	const allIds = TIMELINE_FILTER_OPTIONS.map(option => option.id);
	if (!storedTypes?.length) {
		return allIds;
	}
	const selectedIds = TIMELINE_FILTER_OPTIONS.filter(option =>
		option.types.some(type => storedTypes.includes(type))
	).map(option => option.id);
	// Matches the timeline's own empty-means-all fallback, so the checkboxes can never disagree with it.
	return selectedIds.length > 0 ? selectedIds : allIds;
};

function _ContactActivity({ className, innerRef, contact, setShouldStartPollingForRelationshipUpdate }: Props) {
	const userSession = useUserSession();
	const contactAutomationsProgressBannerComponentRef = useRef<IContactAutomationsProgressBannerComponent>(null);
	const [selectedTab, setSelectedTab] = useState<ContactActivityDataContext>(
		userSession.account?.features?.timeline?.enabled
			? ContactActivityDataContext.Timeline
			: ContactActivityDataContext.RichContent
	);
	const [richContentTypes, setRichContentTypes] = useState<RichContentSupportedTypes[]>(getInitialRichContentTypes());
	const [timelineEventTypes, setTimelineEventTypes] = useState<TimelineEventTypes[]>(
		getInitialTimelineSupportedTypes()
	);
	const [timelineFilterIds, setTimelineFilterIds] = useState<string[]>(getSelectedTimelineFilterIds);
	const [expandAllTimeline, setExpandAllTimeline] = useState<boolean>(() => {
		const storedState = window.localStorage.getItem(expandedToggleStorageKey);
		return storedState === 'true';
	});

	useImperativeHandle(innerRef, () => {
		return {
			showRichContentTab: (options?: { manageAutomationId?: string }) => {
				setSelectedTab(ContactActivityDataContext.RichContent);
				if (options?.manageAutomationId) {
					contactAutomationsProgressBannerComponentRef.current?.showManageModal(options.manageAutomationId);
				}
			},
		};
	}, [setSelectedTab]);
	const canViewContactHistory =
		!!userSession?.user?.emailProviderFeatures?.calendar?.canViewHistoryByContact ||
		!!userSession.user?.emailProviderFeatures?.email?.canViewHistoryByContact;
	const shouldShowTimeline = !!userSession.account.features?.timeline?.enabled;
	const { policiesEnabled, donationsEnabled, opportunitiesEnabled } = userSession.account.features?.boards ?? {};
	const isFilesTabEnabled =
		isFilesFeatureEnabled(userSession.account) &&
		!!userSession.user?.userGroupPermissions?.featurePermissions?.files?.read;

	const handleExpandToggle = (checked: boolean) => {
		window.localStorage.setItem(expandedToggleStorageKey, checked.toString());
		setExpandAllTimeline(checked);
	};
	const onTimelineFilterCommit = (selectedIds: string[]) => {
		const selected = TIMELINE_FILTER_OPTIONS.filter(option => selectedIds.includes(option.id)).flatMap(
			option => option.types
		);
		try {
			window.localStorage.setItem(typesStorageKey, selected.join('|'));
		} catch {
			// Storage unavailable (private mode / quota) — the filter still applies for this session.
		}
		setTimelineFilterIds(selectedIds);
		setTimelineEventTypes(selected.length > 0 ? selected : ContactTimelineTypes);
	};

	const onRichContentFilterCommit = (selectedIds: string[]) => {
		window.localStorage.setItem(richContentStorageKey, selectedIds.join('|'));
		setRichContentTypes(selectedIds as RichContentSupportedTypes[]);
	};
	return (
		<div className={css(styleSheet.contactActivity, className)}>
			<TabRoot
				className={css(styleSheet.tabRoot)}
				value={selectedTab}
				onValueChange={value => setSelectedTab(value as ContactActivityDataContext)}
			>
				<TabList styles={[styleSheet.tabs]}>
					{shouldShowTimeline ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.Timeline} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<NotesIcon fillColor='currentColor' />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>History</span>
						</TabTrigger>
					) : null}
					{!shouldShowTimeline ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.RichContent} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<NotesIcon fillColor='currentColor' />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>History</span>
						</TabTrigger>
					) : null}
					{!shouldShowTimeline && canViewContactHistory ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.Analysis} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<AnalysisIcon />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Analysis</span>
						</TabTrigger>
					) : null}
					{userSession.userRole !== 'limitedUser' ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.Connections} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<ConnectionsIcon />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Connections</span>
						</TabTrigger>
					) : null}
					{isFilesTabEnabled && (
						<TabTrigger
							variant='none'
							value={ContactActivityDataContext.Files}
							styles={[styleSheet.tabTrigger, styleSheet.tabTriggerFile]}
						>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<FileTextIcon />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Files</span>
						</TabTrigger>
					)}
					{policiesEnabled || donationsEnabled || opportunitiesEnabled ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.DataBoards} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<DataboardsIcon fill='currentColor' />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Data Boards</span>
						</TabTrigger>
					) : null}
				</TabList>
				<TabContent value={ContactActivityDataContext.Timeline} asChild>
					<>
						<QuickActions
							contact={contact?.toJs()}
							onContentCreated={() => {
								invalidateContactTimeline({ contactId: contact?.id });
							}}
						/>
						<ContactAutomationsProgressBanner
							contact={contact!}
							onRef={r => {
								contactAutomationsProgressBannerComponentRef.current = r;
							}}
						/>
						<div className={css(styleSheet.timelineFilterOptionsContainer)}>
							<div className={css(styleSheet.expandToggle)}>
								<Checkbox
									id='timeline-expand-all'
									checked={expandAllTimeline}
									onCheckedChange={checked => handleExpandToggle(checked === true)}
								/>
								<Label htmlFor='timeline-expand-all' styles={[styleSheet.expandToggleLabel]}>
									Expand all
								</Label>
							</div>
							<TimelineFilter
								options={TIMELINE_FILTER_OPTIONS}
								selectedIds={timelineFilterIds}
								onCommit={onTimelineFilterCommit}
							/>
						</div>
						<ContactTimelineV2
							contactId={contact?.id}
							contactName={getContactFullName(contact!)}
							emailAddresses={contact?.emailAddresses}
							onDeleteRichContent={(richContent: RichContentViewModel) => contact?.deleteRichContent(richContent)}
							eventTypes={timelineEventTypes}
							expandAllTimeline={expandAllTimeline}
							setShouldStartPollingForRelationshipUpdate={setShouldStartPollingForRelationshipUpdate!}
						/>
					</>
				</TabContent>
				<TabContent value={ContactActivityDataContext.RichContent}>
					<div className={css(styleSheet.richContentFilterOptionsContainer)}>
						<CheckboxFilterDropdown
							ariaLabel='Filter notes and actions by type'
							onCommit={onRichContentFilterCommit}
							options={RICH_CONTENT_FILTER_OPTIONS}
							selectedIds={richContentTypes}
						/>
					</div>
					<ContactAutomationsProgressBanner
						contact={contact!}
						onRef={r => {
							contactAutomationsProgressBannerComponentRef.current = r;
						}}
					/>
					<EntityRichContent entity={contact} supportedTypes={richContentTypes} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.Analysis}>
					<EntityAnalysis entity={contact!} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.Connections}>
					<ContactConnections contact={contact!} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.DataBoards}>
					<EntityDataBoards entityViewModel={contact} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.Files}>
					<ContactFiles contactId={contact?.id} />
				</TabContent>
			</TabRoot>
		</div>
	);
}

export const ContactActivity = observer(_ContactActivity);
